/**
 * Premium "Aperture Open" intro.
 *
 * Visual design:
 *  - Camera-iris with 8 metallic blades closed at start; rotates and opens
 *    revealing the brand logo + headline behind. After full open, the iris
 *    ring stays as a thin accent ring around the content.
 */
import type { SceneBuilder } from "../../reel/scenes.js";
import { brandBackdrop } from "../../primitives/brandBackdrop.js";
import {
  accentFor, brandLogoImg, clipOutroFade, combineSceneFragments, escapeHtml,
  makeTl, typeSize, weights, withAlpha, colors as themeColors,
} from "../../reel/sceneShared.js";

export const apertureOpen: SceneBuilder = ({
  ctx, clipPrefix, inputs, clipDurationSec, clipStartSec,
}) => {
  const accent = accentFor(inputs);
  const isVertical = ctx.format === "9:16";
  const headline = inputs.text ?? "Allyver";
  const sub = inputs.subtext ?? "";
  const headSize = typeSize(isVertical ? "hero" : "mega", ctx.format) * 0.6;
  const subSize = typeSize("md", ctx.format) * 0.55;
  const irisSize = isVertical ? 1300 : 1500;
  const logoH = isVertical ? 200 : 160;

  const t = makeTl(clipStartSec);
  const wrapId = `${clipPrefix}-wrap`;
  const irisId = `${clipPrefix}-iris`;
  const ringId = `${clipPrefix}-ring`;
  const logoId = `${clipPrefix}-logo`;
  const headId = `${clipPrefix}-head`;
  const subId = `${clipPrefix}-sub`;

  const backdrop = brandBackdrop(ctx, `${clipPrefix}-bg`, { variant: "cinematic", accent });

  const logoMarkup = brandLogoImg({ id: `${logoId}-img`, sizePx: logoH });

  // 8 iris blades — each is a triangle positioned around the center.
  // The OUTER wrapper carries the per-blade rotation (CSS, never touched by
  // GSAP). The INNER element is what GSAP scales — this avoids GSAP's
  // transform overwriting the rotation, which would collapse the iris into
  // a single direction.
  const bladeCount = 8;
  const blades = Array.from({ length: bladeCount })
    .map((_, i) => {
      const angle = (i / bladeCount) * 360;
      return `<div style="position:absolute;left:50%;top:50%;width:${irisSize}px;height:${irisSize}px;margin-left:-${irisSize / 2}px;margin-top:-${irisSize / 2}px;transform-origin:center center;transform:rotate(${angle}deg);">
        <div class="${clipPrefix}-blade" style="position:absolute;left:50%;top:0;width:0;height:0;margin-left:-${irisSize * 0.32}px;border-left:${irisSize * 0.32}px solid transparent;border-right:${irisSize * 0.32}px solid transparent;border-top:${irisSize * 0.62}px solid ${themeColors.bg};filter:drop-shadow(0 0 6px rgba(0,0,0,0.6));transform-origin:50% 0;"></div>
      </div>`;
    })
    .join("");

  const html = `<div id="${wrapId}" class="clip" data-start="${clipStartSec.toFixed(4)}" data-duration="${clipDurationSec.toFixed(4)}" style="position:absolute;inset:0;overflow:hidden;">
  <div style="position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:${isVertical ? 36 : 28}px;padding:96px;">
    <div id="${logoId}" style="filter:drop-shadow(0 12px 30px ${withAlpha(accent, 0.5)});">${logoMarkup}</div>
    <div id="${headId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${headSize}px;line-height:0.95;letter-spacing:-3px;color:${themeColors.fg};text-align:center;text-wrap:balance;max-width:${isVertical ? 920 : 1500}px;text-shadow:0 0 60px ${withAlpha(accent, 0.5)};">${escapeHtml(headline)}</div>
    ${sub ? `<div id="${subId}" style="font-family:'Inter',sans-serif;font-weight:${weights.semibold};font-size:${subSize}px;letter-spacing:0.4px;color:${withAlpha(themeColors.fg, 0.8)};text-align:center;max-width:${isVertical ? 760 : 1100}px;line-height:1.35;">${escapeHtml(sub)}</div>` : ""}
  </div>

  <div id="${irisId}" style="position:absolute;inset:0;pointer-events:none;z-index:5;">${blades}</div>
  <div id="${ringId}" style="position:absolute;left:50%;top:50%;width:${irisSize * 0.55}px;height:${irisSize * 0.55}px;margin-left:-${irisSize * 0.275}px;margin-top:-${irisSize * 0.275}px;border-radius:9999px;border:2px solid ${withAlpha(accent, 0.55)};box-shadow:0 0 40px ${withAlpha(accent, 0.45)}, inset 0 0 40px ${withAlpha(accent, 0.18)};opacity:0;pointer-events:none;z-index:6;"></div>
</div>`;

  const init = [
    `gsap.set("#${irisId}", { rotation: -25, transformOrigin: "50% 50%" });`,
    `gsap.set(".${clipPrefix}-blade", { scale: 1, transformOrigin: "50% 50%" });`,
    `gsap.set("#${logoId}", { opacity: 0, scale: 0.7 });`,
    `gsap.set("#${headId}", { opacity: 0, y: 24 });`,
    sub ? `gsap.set("#${subId}", { opacity: 0, y: 18 });` : "",
    `gsap.set("#${ringId}", { opacity: 0, scale: 0.6 });`,
  ].filter(Boolean).join("\n");

  const tweens: string[] = [
    // Iris rotates AND blades scale outward (outer → reveals center).
    t.to(`#${irisId}`, { rotation: 25, duration: 1.05, ease: "raw:\"power3.inOut\"" }, 0.05),
    t.to(`.${clipPrefix}-blade`, { scale: 2.4, duration: 1.05, ease: "raw:\"power3.inOut\"", transformOrigin: "50% 50%" }, 0.05),
    // Logo + headline reveal as iris opens.
    t.to(`#${logoId}`, { opacity: 1, scale: 1, duration: 0.7, ease: "raw:\"back.out(1.5)\"" }, 0.7),
    t.to(`#${headId}`, { opacity: 1, y: 0, duration: 0.55, ease: "raw:\"power3.out\"" }, 0.95),
  ];
  if (sub) tweens.push(t.to(`#${subId}`, { opacity: 1, y: 0, duration: 0.5, ease: "raw:\"power2.out\"" }, 1.15));
  // Ring lights up after iris opens.
  tweens.push(t.to(`#${ringId}`, { opacity: 1, scale: 1, duration: 0.7, ease: "raw:\"power3.out\"" }, 1.0));

  // Slow zoom on logo for premium feel.
  const dwell = Math.max(0.6, clipDurationSec - 1.6);
  tweens.push(t.to(`#${logoId}`, { scale: 1.05, duration: dwell, ease: "raw:\"sine.inOut\"" }, 1.4));
  tweens.push(t.to(`#${ringId}`, { rotation: 90, duration: dwell, ease: "raw:\"none\"" }, 1.4));

  tweens.push(clipOutroFade(clipPrefix, clipDurationSec, clipStartSec, 10));

  return combineSceneFragments([backdrop, { html, init, tweens }]);
};
